import { FC } from "react";

import {
  useAssessedCountDataWithNotSubmitted,
  useAssessmentMode,
  useAssessmentPublishActions,
} from "../../../../utils/assessment";

import { AIAssessmentEvaluationButton } from "./ai-evaluation-report/AIAssessmentEvaluationButton";
import { useAIAssessmentEvaluationAvailability } from "./ai-evaluation-report/use-ai-assessment-evaluation-availability";

import { SECONDARY_OVERLAY_QUERY_PARAM } from "components/constants";
import {
  getMeanScoreByPageOutcomeId,
  showUnpublishAssessmentModal,
} from "components/materials/page/assessment/helpers";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { displayOutcomeName } from "components/outcomes/helpers";
import { OutcomeAssessmentType, PageType } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { CloseButton } from "mds/components/CloseButton";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AssessPointsMarkerIcon, ScoreIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionIsArchived,
  selectNestedPageOutcomes,
  selectOfficialFullSubmissionsByPageId,
} from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { useListSelector } from "store/store-hooks";
import { useQueryParamsActions } from "utils/routing";

interface AssessmentSummaryProps {
  page: PageType;
}

export const AssessmentSummary: FC<AssessmentSummaryProps> = ({ page }) => {
  const { deleteQueryParams } = useQueryParamsActions();
  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, page.id));
  const officialSubmissions = useListSelector((s) =>
    selectOfficialFullSubmissionsByPageId(s, page?.id),
  );
  const assessmentStatusInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page.id));
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);

  const { unpublishGrades } = useAssessmentPublishActions(page);
  const { startAssessing } = useAssessmentMode(page);
  const { count, total, notSubmittedCount } = useAssessedCountDataWithNotSubmitted(page);

  const isAllAssessed = count === total;

  const outcomeAssessments = officialSubmissions
    .map((s) => s.outcome_assessments)
    .flat() as OutcomeAssessmentType[];
  const meanScoreByPageOutcomeId = getMeanScoreByPageOutcomeId(outcomeAssessments);
  const lateUnassessedCount = assessmentStatusInfo?.unassessed_late_submissions_count ?? 0;
  const { humanAssessedSubmissions, shouldShowEvaluationButton } =
    useAIAssessmentEvaluationAvailability(officialSubmissions);

  return (
    <OverlayContainer className="bg-white">
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex items-center gap-2">
          <AssessPointsMarkerIcon />
          <h3 className="m-0">{t("glossary.assessment")}</h3>
        </div>

        <CloseButton onClick={() => deleteQueryParams([SECONDARY_OVERLAY_QUERY_PARAM])} />
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false}>
        <div className="mt-2 flex flex-col gap-4">
          <div className="text-sm text-black-tint-20">
            <Trans
              components={{
                b: <span className="font-semibold" />,
              }}
              i18nKey={`assessment.assessment_summary.description_${isAllAssessed ? "all" : "some"}`}
              t={t}
              values={{
                count,
                total,
                notSubmitted: notSubmittedCount,
                lateUnassessedCount,
              }}
            />
          </div>

          <SquareButton
            className="w-full"
            disabled={isArchived}
            kind="tertiary"
            size="m"
            onClick={() => startAssessing()}
          >
            {isAllAssessed ? t("assessment.edit_assessments") : t("assessment.resume_assessment")}
          </SquareButton>

          {page.assessments_published_at && (
            <SquareButton
              className={
                outcomeAssessments.length > 0 && !shouldShowEvaluationButton
                  ? "mb-2 w-full"
                  : "w-full"
              }
              disabled={isArchived}
              eventName="Assessment - Unpublish all"
              kind="tertiary"
              size="m"
              onClick={() => showUnpublishAssessmentModal(count, unpublishGrades)}
            >
              {t("assessment.unpublish_all")}
            </SquareButton>
          )}

          {shouldShowEvaluationButton && (
            <AIAssessmentEvaluationButton
              hasOutcomeAssessments={outcomeAssessments.length > 0}
              humanAssessedSubmissions={humanAssessedSubmissions}
              page={page}
              pageOutcomes={pageOutcomes}
            />
          )}

          {outcomeAssessments.length > 0 ? (
            <>
              <HorizontalDivider />

              <div className="mt-2 text-sm text-black-tint-20">
                {t("assessment.assessment_summary.outcome_summary")}
              </div>

              <div className="flex w-full flex-col gap-1">
                {pageOutcomes.map((pageOutcome) => (
                  <div className="flex flex-row items-center justify-between" key={pageOutcome.id}>
                    <OutcomeChip title={displayOutcomeName(pageOutcome.outcome)} />
                    <ScoreIcon size="m" value={meanScoreByPageOutcomeId[pageOutcome.id]} />
                  </div>
                ))}
              </div>

              <SquareButton
                className="w-full"
                kind="tertiary"
                size="m"
                onClick={() => deleteQueryParams([SECONDARY_OVERLAY_QUERY_PARAM])}
              >
                {t("common.close")}
              </SquareButton>
            </>
          ) : null}
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
